import { en, type TranslationResource } from './locales/en';
import { fr } from './locales/fr';
import { de } from './locales/de';
import { es } from './locales/es';
import { it } from './locales/it';
import { pt } from './locales/pt';
import { pl } from './locales/pl';
import { tr } from './locales/tr';
import { ar } from './locales/ar';
import { jp } from './locales/jp';
import { kr } from './locales/kr';
import CSInterface from '../lib/cep/csinterface';

export const DEFAULT_LANGUAGE = 'en';

const resources = {
  en: { translation: en },
  fr: { translation: fr },
  de: { translation: de },
  es: { translation: es },
  it: { translation: it },
  pt: { translation: pt },
  pl: { translation: pl },
  tr: { translation: tr },
  ar: { translation: ar },
  jp: { translation: jp },
  kr: { translation: kr },
} as const;

type SupportedLanguage = keyof typeof resources;

export type TranslationKey = keyof TranslationResource;
export type InterpolationParams = Record<string, string | number>;

const RTL_LANGUAGES: ReadonlySet<SupportedLanguage> = new Set(['ar']);

const ADOBE_LOCALE_TO_LANGUAGE: Partial<Record<string, SupportedLanguage>> = {
  en_US: 'en',
  en_GB: 'en',
  fr_FR: 'fr',
  de_DE: 'de',
  es_ES: 'es',
  it_IT: 'it',
  pt_BR: 'pt',
  pt_PT: 'pt',
  pl_PL: 'pl',
  tr_TR: 'tr',
  ar_AE: 'ar',
  ja_JP: 'jp',
  ko_KR: 'kr',
};

const LANGUAGE_CODE_ALIASES: Partial<Record<string, SupportedLanguage>> = {
  en: 'en',
  fr: 'fr',
  de: 'de',
  es: 'es',
  it: 'it',
  pt: 'pt',
  pl: 'pl',
  tr: 'tr',
  ar: 'ar',
  ja: 'jp',
  ko: 'kr',
  jp: 'jp',
  kr: 'kr',
};

const resolveLanguageFromHostLocale = (
  appUILocale: string
): SupportedLanguage => {
  const trimmedLocale = appUILocale.trim();
  if (!trimmedLocale) {
    return DEFAULT_LANGUAGE;
  }

  const mappedLocale = ADOBE_LOCALE_TO_LANGUAGE[trimmedLocale];
  if (mappedLocale) {
    return mappedLocale;
  }

  const languageCode = trimmedLocale.split(/[-_]/)[0]?.toLowerCase();
  if (!languageCode) {
    return DEFAULT_LANGUAGE;
  }

  const mappedLanguage = LANGUAGE_CODE_ALIASES[languageCode];
  if (mappedLanguage) {
    return mappedLanguage;
  }

  return DEFAULT_LANGUAGE;
};

/**
 * Falls back to English rather than throwing: this runs at module scope, so a
 * throw here aborts the whole bundle and the panel never downloads the
 * installer — which is its only job. An untranslated panel still works.
 */
const getDefaultLanguage = (): SupportedLanguage => {
  try {
    const csInterface = new CSInterface();
    const hostEnv = csInterface.getHostEnvironment();

    if (!hostEnv?.appUILocale) {
      console.warn(
        '[i18n] Missing host environment appUILocale, falling back to',
        DEFAULT_LANGUAGE
      );
      return DEFAULT_LANGUAGE;
    }

    return resolveLanguageFromHostLocale(hostEnv.appUILocale);
  } catch (error) {
    console.warn(
      '[i18n] Failed to read the host locale, falling back to',
      DEFAULT_LANGUAGE,
      error
    );
    return DEFAULT_LANGUAGE;
  }
};

const interpolate = (
  template: string,
  params?: InterpolationParams
): string => {
  if (!params) {
    return template;
  }

  return template.replace(/\{\{(\w+)\}\}/g, (_match, name: string) => {
    const value = params[name];
    if (value === undefined) {
      throw new Error(`Missing interpolation param: ${name}`);
    }
    return String(value);
  });
};

const createI18n = (language: SupportedLanguage) => {
  const table = resources[language].translation;
  const fallback = resources[DEFAULT_LANGUAGE].translation;

  const t = (key: TranslationKey, params?: InterpolationParams): string =>
    interpolate(table[key] ?? fallback[key] ?? key, params);

  return {
    language,
    t,
    dir: (): 'ltr' | 'rtl' => (RTL_LANGUAGES.has(language) ? 'rtl' : 'ltr'),
  } as const;
};

export const i18n = createI18n(getDefaultLanguage());
export type I18n = typeof i18n;
